import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Section, SectionHeader } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { Tilt3D } from "@/components/Tilt3D";
import { FAQ } from "@/components/FAQ";
import { whatsappUrl } from "@/lib/site";
import {
  FileText, PenLine, Newspaper, TrendingUp, MapPin, Search, Sparkles,
  ShieldCheck, Building2, Check, X, Star, ArrowRight, Globe2, MessageCircle,
  ExternalLink, Infinity as InfinityIcon,
} from "lucide-react";
import { BRAZIL_STATES, BRAZIL_MAP_WIDTH, BRAZIL_MAP_HEIGHT } from "@/lib/brazil-map-data";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import resultado1 from "@/assets/resultado_1.png.asset.json";
import resultado2 from "@/assets/resultado_2.png.asset.json";
import resultado3 from "@/assets/resultado_3.png.asset.json";
import resultado4 from "@/assets/resultado_4.png.asset.json";
import resultado5 from "@/assets/resultado_5.png.asset.json";
import resultado6 from "@/assets/resultado_6.png.asset.json";

const WA = whatsappUrl("Olá! Tenho interesse no Plano Autoridade Local da SVG Multimídia.");
const SUPPORT_WA_NUMBER = "555596852045";
const SUPPORT_WA = `https://wa.me/${SUPPORT_WA_NUMBER}?text=${encodeURIComponent(
  "Olá! Sou cliente do Plano Autoridade Local da SVG e preciso de suporte / atualizações."
)}`;

const PORTALS: { name: string; url: string; badge?: string }[] = [
  { name: "Lista Empresa BR", url: "https://www.listaempresabr.com.br/" },
  { name: "SVG News", url: "https://svgnews.com.br/" },
  { name: "Guia Negócio Brasil", url: "https://guianegociobrasil.com.br/" },
  { name: "Brasil Empresa Online", url: "https://www.empresasbrasilonline.com.br/" },
  { name: "Decoradíssimo", url: "https://decoradissimo.com.br/", badge: "+10 anos online" },
  { name: "Gazeta Regional Online", url: "https://gazetaregionalonline.com.br/", badge: "+10 anos online" },
  { name: "Liderança com Valores", url: "https://liderancacomvalores.com.br/", badge: "+8 anos online" },
  { name: "Concurso Astor", url: "https://concursoastor.com.br/", badge: "+6 anos online" },
];

export const Route = createFileRoute("/plano-autoridade-local")({
  head: () => {
    const url = "https://www.svgmultimidia.com.br/plano-autoridade-local";
    const title = "Plano Autoridade Local | Notícias mensais sobre sua empresa em portais nacionais";
    const description =
      "A SVG cria e publica de 4 a 8 notícias por mês sobre sua empresa em portais nacionais, com foco na sua cidade, região e serviços. Por R$ 997/mês.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "product" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: url },
        // Preconnect + dns-prefetch para os 4 portais nacionais parceiros —
        // acelera a navegação quando o visitante clica nos botões.
        ...PORTALS.flatMap((p) => {
          const origin = new URL(p.url).origin;
          return [
            { rel: "preconnect", href: origin, crossOrigin: "anonymous" as const },
            { rel: "dns-prefetch", href: origin },
          ];
        }),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: "Plano Autoridade Local",
            description,
            brand: { "@type": "Brand", name: "SVG Multimídia" },
            offers: [
              { "@type": "Offer", name: "Mensal", price: "997.00", priceCurrency: "BRL", url },
            ],
          }),
        },
      ],
    };
  },
  component: PlanoAutoridadeLocalPage,
});

function PlanoAutoridadeLocalPage() {
  return (
    <>
      <Hero />
      <WhatIs />
      <HowItWorks />
      <PortalsSection />
      <NationalLocal />
      <Included />
      <Results />
      <BeforeAfter />
      <AdsCompare />
      <Pricing />
      <ForWho />
      <NeedToStart />
      <ResultsCarousel />
      <Testimonials />
      <Section>
        <FAQ
          items={[
            { q: "O plano coloca minha empresa em primeiro lugar no Google?", a: "Não prometemos posição específica no Google. O objetivo do plano é fortalecer sua presença online com publicações mensais sobre sua empresa em portais nacionais." },
            { q: "As notícias são publicadas onde?", a: "As notícias são publicadas em portais online nacionais da rede de conteúdo da SVG." },
            { q: "O conteúdo é sobre qualquer assunto?", a: "Não. O conteúdo é criado com foco na sua empresa, serviços, cidade, região de atendimento e diferenciais." },
            { q: "Eu preciso escrever os textos?", a: "Não. Você envia os dados da empresa e a SVG cuida da criação, organização e publicação dos conteúdos." },
            { q: "O plano inclui criação da ficha do Google?", a: "Não. A criação ou otimização da Ficha do Google é um serviço separado. O Plano Autoridade Local é focado na criação e publicação de notícias mensais em portais nacionais." },
            { q: "Posso cancelar o plano mensal?", a: "Sim. O plano mensal pode ser mantido enquanto fizer sentido para sua empresa. As notícias publicadas permanecem online para sempre, mesmo após o cancelamento." },
            { q: "Qual plano é mais indicado?", a: "O plano anual é o mais indicado para empresas que querem construir presença online com consistência, já que autoridade digital é um trabalho contínuo. Valores do plano anual são consultados diretamente pelo WhatsApp." },
            { q: "Esses portais têm autoridade no Google?", a: "Sim. Trabalhamos com portais nacionais que já possuem tempo de domínio, histórico de publicações e boa indexação no Google, o que ajuda as notícias a aparecerem nas buscas." },
            { q: "Vocês têm exemplos de clientes e resultados reais?", a: "Sim. Podemos enviar exemplos reais de publicações e prints de resultados de clientes atendidos. Chame nossa equipe no WhatsApp que enviamos os cases mais próximos do seu segmento." },
            { q: "As notícias aparecem ao pesquisar o nome da empresa ou palavras-chave como 'advogada em Manaus', 'psicoterapeuta em Manaus'?", a: "As notícias são otimizadas para aparecerem principalmente em buscas pelo nome da empresa, do profissional e por termos que combinam serviço + cidade/região. Não prometemos posição garantida no Google, mas trabalhamos para ampliar essa presença." },
            { q: "O conteúdo é exclusivo ou gerado por IA e reutilizado para vários clientes?", a: "O conteúdo é criado especificamente para a sua empresa, com foco nos seus serviços, cidade, região e diferenciais. Não reutilizamos matérias entre clientes." },
            { q: "Quem escreve as matérias? Há revisão jornalística?", a: "As matérias são produzidas pela equipe de conteúdo da SVG, com estrutura jornalística e revisão antes da publicação em cada portal." },
            { q: "As publicações permanecem no ar permanentemente ou podem ser removidas?", a: "Sim. Todas as notícias publicadas são vitalícias e permanecem online para sempre, independentemente do plano contratado (mensal ou anual), mesmo após o término do contrato." },
            { q: "As matérias incluem links para o site, perfil do Google e redes sociais?", a: "Sim. Sempre que fizer sentido para o conteúdo, as matérias podem citar e apontar para o seu site, ficha do Google e principais redes sociais, ajudando a reforçar a presença digital da empresa." },
            { q: "Há fidelidade ou posso cancelar a qualquer momento?", a: "O plano mensal não tem fidelidade e pode ser cancelado quando quiser. O plano anual é uma contratação de 12 meses, com valor consultado pelo WhatsApp. Em ambos os planos, as notícias publicadas permanecem online para sempre." },
            { q: "Há garantia de resultados ou apenas a publicação das notícias?", a: "Não prometemos resultados garantidos como posição no Google ou volume de clientes. A entrega contratada é a criação e a publicação das notícias mensais nos portais nacionais, com foco em construir presença, autoridade e confiança online ao longo do tempo." },
            { q: "Vocês têm cases de advogados, psicólogos ou profissionais da saúde?", a: "Sim. Já atendemos advogados, psicólogos, terapeutas, clínicas e profissionais da área da saúde. Chame no WhatsApp que enviamos exemplos de publicações desses segmentos." },
          ]}
        />
      </Section>
      <FinalCTA />
    </>
  );
}

/* ───────────── Hero ───────────── */
function Hero() {
  return (
    <section className="hero-bg relative overflow-hidden">
      <div className="container-svg pt-12 pb-16 md:pt-20 md:pb-24">
        <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "Plano Autoridade Local" }]} />
        <div className="mt-8 grid items-center gap-12 lg:grid-cols-[1.05fr_1fr]">
          <div>
            <span className="eyebrow">
              <Sparkles className="size-3.5" /> Plano Autoridade Local
            </span>
            <h1 className="mt-5 text-4xl font-bold leading-[1.05] tracking-tight md:text-5xl lg:text-6xl">
              <span className="text-gradient">
                Sua empresa publicada em portais nacionais todos os meses
              </span>
            </h1>
            <p className="mt-5 max-w-xl text-base text-muted-foreground md:text-lg">
              A SVG cria e publica notícias sobre sua empresa em portais online nacionais, com conteúdo focado na sua cidade, região e serviços.
            </p>
            <p className="mt-4 max-w-xl text-sm text-muted-foreground md:text-base">
              Fortaleça sua presença no Google, aumente a confiança de quem pesquisa e crie mais caminhos para clientes encontrarem sua empresa.
            </p>

            <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-[oklch(0.82_0.16_85/0.5)] bg-[oklch(0.82_0.16_85/0.16)] px-4 py-1.5 text-sm font-semibold text-[oklch(0.52_0.13_72)]">
              <Star className="size-3.5 fill-current" />
              R$ 997/mês
            </div>

            <div className="mt-7 flex flex-wrap gap-3">
              <a href={WA} target="_blank" rel="noopener" className="btn-primary">
                <MessageCircle className="size-4" /> Contratar pelo WhatsApp <ArrowRight className="size-4" />
              </a>
            </div>
          </div>

          <Reveal delay={1}>
            <HeroMockup />
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function HeroMockup() {
  return (
    <div className="relative mx-auto w-full max-w-lg">
      <div aria-hidden className="absolute -inset-8 -z-10 rounded-[3rem] bg-[radial-gradient(60%_60%_at_50%_40%,oklch(0.62_0.22_258/0.35),transparent_70%)] blur-2xl" />

      {/* Central "Sua empresa" node */}
      <div className="relative mx-auto flex aspect-square max-w-[440px] items-center justify-center">
        {/* Orbit rings */}
        <div aria-hidden className="absolute inset-4 rounded-full border border-ink/10" />
        <div aria-hidden className="absolute inset-16 rounded-full border border-ink/10" />

        {/* Center pill */}
        <div className="relative z-20 flex flex-col items-center gap-2 rounded-2xl border border-ink/15 bg-gradient-to-b from-[oklch(0.62_0.22_258/0.3)] to-[oklch(0.62_0.22_258/0.08)] px-5 py-4 shadow-[0_20px_60px_-20px_oklch(0.62_0.22_258/0.55)] backdrop-blur">
          <Building2 className="size-6 text-[oklch(0.52_0.13_72)]" />
          <span className="text-sm font-semibold">Sua Empresa</span>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Cidade · Região · Serviços
          </span>
        </div>

        {/* Floating portal chips */}
        <FloatingChip className="-left-2 top-2 float-slow" label="Lista Empresa BR" tone="blue" />
        <FloatingChip className="-right-2 top-8 float-slower" label="SVG News" tone="blue" />
        <FloatingChip className="left-0 bottom-6 float-slower" label="Guia Negócio Brasil" tone="blue" />
        <FloatingChip className="right-0 bottom-16 float-slow" label="Brasil Empresa Online" tone="blue" />
        <FloatingChip className="-right-4 bottom-2 float-slow" label="Gazeta Regional Online" tone="blue" />

        {/* Search engine chips */}
        <FloatingChip className="left-1/2 -top-2 -translate-x-1/2 float-slower" label="Google" tone="gold" icon={<Search className="size-3.5" />} />
        <FloatingChip className="-left-3 top-1/2 -translate-y-1/2 float-slow" label="ChatGPT" tone="gold" icon={<Sparkles className="size-3.5" />} />
        <FloatingChip className="-right-3 top-1/2 -translate-y-1/2 float-slow" label="Bing" tone="gold" icon={<Globe2 className="size-3.5" />} />
      </div>

      {/* News cards */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <NewsCard headline="Sua Empresa amplia atendimento na região" source="Portal Nacional" />
        <NewsCard headline="Novo serviço destaca Sua Empresa na cidade" source="Portal Nacional" />
      </div>
    </div>
  );
}

function FloatingChip({ className = "", label, tone = "blue", icon }: { className?: string; label: string; tone?: "blue" | "gold"; icon?: React.ReactNode }) {
  const toneCls =
    tone === "gold"
      ? "border-[oklch(0.82_0.16_85/0.5)] bg-[oklch(0.82_0.16_85/0.12)] text-[oklch(0.50_0.13_72)]"
      : "border-ink/15 bg-ink/[0.06] text-foreground";
  return (
    <div className={`absolute z-10 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold backdrop-blur ${toneCls} ${className}`}>
      {icon}
      {label}
    </div>
  );
}

function NewsCard({ headline, source }: { headline: string; source: string }) {
  return (
    <div className="card-glass !p-3">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
        <Newspaper className="size-3" /> {source}
      </div>
      <p className="mt-1.5 text-[12.5px] font-semibold leading-snug">{headline}</p>
    </div>
  );
}

/* ───────────── What is ───────────── */
function WhatIs() {
  return (
    <Section>
      <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-center">
        <Reveal>
          <SectionHeader
            eyebrow="Sobre o plano"
            title="O que é o Plano Autoridade Local?"
            description="Um serviço da SVG para empresas que querem construir mais presença online de forma simples, acessível e recorrente."
          />
        </Reveal>
        <Reveal delay={1}>
          <div className="space-y-4 text-muted-foreground md:text-lg">
            <p>
              Todos os meses, criamos de <strong className="text-foreground">4 a 8 notícias</strong> sobre a sua empresa e publicamos em <strong className="text-foreground">portais online nacionais</strong>.
            </p>
            <p>
              Esses conteúdos são escritos com foco na sua cidade, região de atendimento, serviços e diferenciais.
            </p>
            <p>
              O objetivo é fazer sua empresa parecer mais presente, mais confiável e mais profissional para quem pesquisa no Google e em outras ferramentas de busca.
            </p>
            <div className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-5 text-foreground">
              <p className="text-sm font-semibold md:text-base">
                Você envia os dados da empresa. A SVG cria, publica e cuida de tudo.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

/* ───────────── How it works ───────────── */
function HowItWorks() {
  const steps = [
    { n: "1", icon: FileText, t: "Você envia os dados da empresa", d: "Nome, cidade, serviços, região de atendimento, fotos e informações principais." },
    { n: "2", icon: PenLine, t: "A SVG cria as notícias", d: "Nossa equipe transforma as informações em conteúdos profissionais, claros e otimizados." },
    { n: "3", icon: Newspaper, t: "Publicamos em portais nacionais", d: "As notícias são publicadas em portais online da nossa rede de conteúdo." },
    { n: "4", icon: TrendingUp, t: "Sua empresa ganha mais presença online", d: "Mais conteúdos publicados sobre sua empresa ajudam a fortalecer sua autoridade e confiança." },
  ];
  return (
    <Section className="bg-[color:var(--color-sidebar)]">
      <SectionHeader eyebrow="Como funciona" title="Como funciona na prática" center />
      <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {steps.map((s, i) => (
          <Reveal key={s.n} delay={(i % 4) as 0 | 1 | 2 | 3}>
            <Tilt3D>
              <div className="card-glass relative h-full">
                <span className="absolute right-4 top-4 font-display text-4xl font-bold text-primary/25">{s.n}</span>
                <div className="mb-4 inline-flex size-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary/25 to-primary/5 ring-1 ring-primary/30">
                  <s.icon className="size-5 text-primary" />
                </div>
                <h3 className="font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
              </div>
            </Tilt3D>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* ───────────── National + Local ───────────── */
function NationalLocal() {
  return (
    <Section>
      <div className="grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
        <Reveal>
          <div>
            <SectionHeader
              eyebrow="Alcance + Estratégia"
              title="Portais nacionais, conteúdo focado na sua região"
              description="Os portais possuem atuação nacional, mas os conteúdos são criados com foco no seu mercado local."
            />
            <p className="mt-6 text-muted-foreground md:text-lg">
              Isso significa que sua empresa pode ser publicada em portais online nacionais com notícias voltadas para sua cidade, região de atendimento e serviços.
            </p>
            <div className="mt-6 rounded-2xl border border-primary/30 bg-[image:var(--gradient-hero)] p-6">
              <p className="text-2xl font-bold md:text-3xl">
                <span className="text-gradient">Alcance nacional + estratégia local</span>
              </p>
              <p className="mt-3 text-sm text-muted-foreground md:text-base">
                Essa combinação ajuda sua empresa a construir presença digital de forma constante, sem depender apenas de anúncios pagos ou redes sociais.
              </p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={1}>
          <BrazilMap />
        </Reveal>
      </div>
    </Section>
  );
}

function BrazilMap() {
  return (
    <div className="relative mx-auto w-full max-w-md">
      <div className="card-glass !p-6">
        <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground">
          <span>Presença nacional</span>
          <span className="inline-flex items-center gap-1 text-[oklch(0.52_0.13_72)]">
            <MapPin className="size-3" /> 27 estados
          </span>
        </div>
        <div className="relative mt-4 overflow-hidden rounded-xl bg-[oklch(0.14_0.03_260)] p-3">
          <svg
            viewBox={`0 0 ${BRAZIL_MAP_WIDTH} ${BRAZIL_MAP_HEIGHT}`}
            role="img"
            aria-label="Mapa do Brasil com um marcador em cada um dos 27 estados"
            className="h-auto w-full"
          >
            <defs>
              <radialGradient id="stateFill" cx="50%" cy="40%" r="80%">
                <stop offset="0%" stopColor="oklch(0.32 0.06 260)" />
                <stop offset="100%" stopColor="oklch(0.22 0.05 260)" />
              </radialGradient>
              <filter id="pinGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <g>
              {BRAZIL_STATES.map((s) => (
                <path
                  key={s.uf}
                  d={s.d}
                  fill="url(#stateFill)"
                  stroke="oklch(0.55 0.05 260 / 0.7)"
                  strokeWidth={1}
                  strokeLinejoin="round"
                />
              ))}
            </g>
            <g filter="url(#pinGlow)">
              {BRAZIL_STATES.map((s, i) => (
                <g key={s.uf} transform={`translate(${s.cx} ${s.cy})`}>
                  <circle r={9} fill="oklch(0.90 0.14 85 / 0.25)">
                    <animate
                      attributeName="r"
                      values="6;14;6"
                      dur="2.4s"
                      begin={`${(i % 8) * 0.2}s`}
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      values="0.55;0;0.55"
                      dur="2.4s"
                      begin={`${(i % 8) * 0.2}s`}
                      repeatCount="indefinite"
                    />
                  </circle>
                  <circle r={5} fill="oklch(0.90 0.14 85)" stroke="oklch(0.16 0.03 260)" strokeWidth={1.5} />
                  <title>{s.name} ({s.uf})</title>
                </g>
              ))}
            </g>
          </svg>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2 text-center text-[10px] text-muted-foreground">
          {PORTALS.map((p) => (
            <div key={p.name} className="rounded-md border border-ink/10 bg-ink/5 py-1.5">
              {p.name}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ───────────── Portals (real links) ───────────── */
function PortalsSection() {
  return (
    <Section>
      <SectionHeader
        eyebrow="Nossa rede"
        title="Portais parceiros onde publicamos sua empresa"
        description="Conheça os portais nacionais da rede de conteúdo da SVG. Clique para visitar cada um."
        center
      />
      <div className="mx-auto mt-10 grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {PORTALS.map((p, i) => (
          <Reveal key={p.url} delay={(i % 4) as 0 | 1 | 2 | 3}>
            <a
              href={p.url}
              target="_blank"
              rel="noopener"
              className="group card-glass flex h-full flex-col justify-between !p-6 transition hover:-translate-y-1 hover:border-primary/40"
            >
              <div>
                <div className="inline-flex size-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary/25 to-primary/5 ring-1 ring-primary/30">
                  <Newspaper className="size-5 text-primary" />
                </div>
                <h3 className="mt-4 text-base font-semibold leading-snug">{p.name}</h3>
                <p className="mt-1 truncate text-xs text-muted-foreground">
                  {new URL(p.url).hostname.replace(/^www\./, "")}
                </p>
                {p.badge && (
                  <span className="mt-2 inline-flex items-center gap-1 rounded-full border border-[oklch(0.82_0.16_85/0.5)] bg-[oklch(0.82_0.16_85/0.16)] px-2 py-0.5 text-[10px] font-semibold text-[oklch(0.52_0.13_72)]">
                    <Star className="size-3 fill-current" />
                    {p.badge}
                  </span>
                )}
              </div>
              <span className="mt-5 inline-flex items-center gap-1 text-xs font-semibold text-primary transition group-hover:gap-2">
                Visitar portal <ExternalLink className="size-3.5" />
              </span>
            </a>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* ───────────── Included ───────────── */
function Included() {
  const items = [
    "4 a 8 notícias por mês sobre sua empresa",
    "Publicação em portais online nacionais",
    "Conteúdo focado na sua cidade e região",
    "Textos criados pela equipe da SVG",
    "Títulos e estrutura pensados para busca",
    "Publicações encontráveis nos portais parceiros",
    "Suporte para tirar dúvidas sobre as publicações",
    "Tudo feito pela SVG, sem trabalho para você",
  ];
  return (
    <Section className="bg-[color:var(--color-sidebar)]">
      <SectionHeader eyebrow="Incluso" title="O que está incluso no plano" center />
      <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-2">
        {items.map((t, i) => (
          <Reveal as="li" key={t} delay={(i % 3) as 0 | 1 | 2} className="flex items-start gap-3 rounded-xl border border-border shadow-[var(--shadow-soft)] bg-background/40 p-4 text-sm md:text-base">
            <span className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary ring-1 ring-primary/30">
              <Check className="size-3.5" />
            </span>
            <span>{t}</span>
          </Reveal>
        ))}
      </ul>
      <p className="mt-8 text-center text-muted-foreground md:text-lg">
        Você não precisa escrever, publicar ou entender de Google. <strong className="text-foreground">Nós cuidamos da entrega.</strong>
      </p>
    </Section>
  );
}

/* ───────────── Results ───────────── */
function Results() {
  const cards = [
    { icon: Search, t: "Mais presença no Google", d: "Sua empresa passa a ter mais conteúdos publicados na internet, aumentando os pontos de presença quando alguém pesquisa por ela, seus serviços ou sua região." },
    { icon: ShieldCheck, t: "Mais autoridade", d: "Notícias e publicações sobre sua empresa ajudam a criar uma imagem mais profissional e confiável." },
    { icon: Sparkles, t: "Mais confiança para quem pesquisa", d: "Quando uma pessoa encontra conteúdos sobre sua empresa, ela tende a perceber mais credibilidade." },
    { icon: MessageCircle, t: "Mais chances de receber contatos", d: "Com mais presença online, sua empresa cria mais caminhos para possíveis clientes chegarem até você." },
    { icon: TrendingUp, t: "Mais força digital ao longo do tempo", d: "A cada mês, novas publicações vão sendo adicionadas, criando uma presença recorrente e acumulativa." },
  ];
  return (
    <Section>
      <SectionHeader eyebrow="Resultado esperado" title="O que esse trabalho gera para sua empresa" center />
      <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {cards.map((c, i) => (
          <Reveal key={c.t} delay={(i % 3) as 0 | 1 | 2}>
            <Tilt3D>
              <div className="card-glass h-full">
                <div className="mb-4 inline-flex size-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary/25 to-primary/5 ring-1 ring-primary/30">
                  <c.icon className="size-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold">{c.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{c.d}</p>
              </div>
            </Tilt3D>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* ───────────── Before / After ───────────── */
function BeforeAfter() {
  const before = [
    "Poucos conteúdos sobre a empresa",
    "Baixa autoridade online",
    "Dependência de indicação ou anúncio",
    "Pouca presença em buscas",
    "Cliente encontra poucas informações",
  ];
  const after = [
    "Notícias publicadas todos os meses",
    "Mais presença em portais online",
    "Mais autoridade para a marca",
    "Mais confiança para quem pesquisa",
    "Mais chances de gerar contatos",
  ];
  return (
    <Section className="bg-[color:var(--color-sidebar)]">
      <SectionHeader eyebrow="Antes e depois" title="De pouca presença para uma presença digital constante" center />
      <div className="mt-12 grid gap-5 md:grid-cols-2">
        <Reveal>
          <div className="rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-background/50 p-6">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-ink/10 bg-ink/5 px-3 py-1 text-xs uppercase tracking-wider text-muted-foreground">
              Antes
            </div>
            <ul className="space-y-3">
              {before.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-ink/5 text-muted-foreground ring-1 ring-ink/10">
                    <X className="size-3" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={1}>
          <div className="relative rounded-2xl border border-primary/30 bg-gradient-to-b from-primary/10 to-transparent p-6 shadow-[var(--shadow-glow)]">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary-foreground">
              Depois
            </div>
            <ul className="space-y-3">
              {after.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-foreground">
                  <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/25 text-primary ring-1 ring-primary/40">
                    <Check className="size-3" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </Section>
  );
}

/* ───────────── Ads compare ───────────── */
function AdsCompare() {
  const ads = [
    "Você paga para aparecer",
    "Depende de orçamento diário",
    "Parou de investir, o anúncio para",
    "Foco em clique pago",
  ];
  const plan = [
    "Sua empresa ganha publicações mensais",
    "Plano mensal acessível",
    "Conteúdos publicados continuam existindo",
    "Foco em presença, autoridade e confiança",
  ];
  return (
    <Section>
      <SectionHeader
        eyebrow="Comparativo"
        title="Diferente de anúncios pagos"
        description="O Google Ads é uma ótima ferramenta para gerar tráfego pago, mas depende de investimento constante. Quando o orçamento para, a visibilidade dos anúncios também para. O Plano Autoridade Local trabalha de outra forma: ele fortalece sua presença online com publicações mensais sobre sua empresa."
        center
      />
      <div className="mt-12 grid gap-5 md:grid-cols-2">
        <Reveal>
          <div className="rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-background/50 p-6">
            <h3 className="text-lg font-semibold">Google Ads</h3>
            <ul className="mt-4 space-y-3">
              {ads.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-muted-foreground">
                  <span className="mt-0.5 text-muted-foreground">•</span>{t}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={1}>
          <div className="rounded-2xl border border-primary/30 bg-gradient-to-b from-primary/10 to-transparent p-6">
            <h3 className="text-lg font-semibold">
              <span className="text-gradient">Plano Autoridade Local</span>
            </h3>
            <ul className="mt-4 space-y-3">
              {plan.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-foreground">
                  <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
      <p className="mx-auto mt-8 max-w-2xl text-center text-xs text-muted-foreground md:text-sm">
        O Plano Autoridade Local não substitui anúncios pagos, mas é uma forma acessível de construir presença digital todos os meses.
      </p>
    </Section>
  );
}

/* ───────────── Pricing ───────────── */
function Pricing() {
  const features = [
    "4 a 8 notícias mensais",
    "Publicação em portais nacionais",
    "Conteúdo focado na cidade e região",
    "Sem necessidade de produzir conteúdo",
    "Notícias vitalícias — permanecem online para sempre",
    "Suporte direto pelo WhatsApp",
  ];
  return (
    <Section id="planos" className="bg-[color:var(--color-sidebar)]">
      <SectionHeader eyebrow="Planos" title="Escolha seu plano" center />
      <div className="mx-auto mt-12 grid max-w-5xl gap-6 md:grid-cols-2">
        <Reveal>
          <div className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-primary/40 bg-gradient-to-b from-primary/15 via-transparent to-transparent p-8 shadow-[var(--shadow-glow)]">
            <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
            <div className="text-xs font-semibold uppercase tracking-wider text-primary">Plano Mensal</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="text-4xl font-bold md:text-5xl">R$ 997</span>
              <span className="text-muted-foreground">/mês</span>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Publicações mensais em portais nacionais para fortalecer sua presença online e a autoridade da sua empresa no Google.
            </p>
            <ul className="mt-6 space-y-3">
              {features.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm">
                  <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                  {t}
                </li>
              ))}
            </ul>
            <a href={WA} target="_blank" rel="noopener" className="btn-primary mt-8 w-full">
              <MessageCircle className="size-4" /> Contratar pelo WhatsApp <ArrowRight className="size-4" />
            </a>
            <p className="mt-3 text-center text-[11px] text-muted-foreground">
              Falamos com você para entender sua empresa e iniciar as publicações.
            </p>
          </div>
        </Reveal>

        <Reveal delay={1}>
          <div className="card-glass relative flex h-full flex-col !p-8">
            <span className="absolute right-6 top-6 inline-flex items-center gap-1 rounded-full bg-[oklch(0.82_0.16_85/0.18)] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[oklch(0.50_0.13_72)] ring-1 ring-[oklch(0.82_0.16_85/0.4)]">
              <Star className="size-3 fill-current" /> Melhor custo-benefício
            </span>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Plano Anual</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="text-3xl font-bold md:text-4xl">Consultar valores</span>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Contratação de 12 meses com condição especial. Fale com a equipe SVG pelo WhatsApp para receber a proposta personalizada para sua empresa.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "12 meses de publicações contínuas",
                "4 a 8 notícias por mês",
                "Notícias vitalícias — permanecem online para sempre",
                "Planejamento contínuo de autoridade digital",
                "Condição comercial diferenciada",
                "Suporte prioritário pelo WhatsApp",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm">
                  <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                  {t}
                </li>
              ))}
            </ul>
            <a
              href={whatsappUrl("Olá! Quero consultar valores do plano anual do Autoridade Local da SVG Multimídia.")}
              target="_blank"
              rel="noopener"
              className="btn-ghost mt-8 w-full"
            >
              <MessageCircle className="size-4" /> Consultar valor no WhatsApp <ArrowRight className="size-4" />
            </a>
            <p className="mt-3 text-center text-[11px] text-muted-foreground">
              Proposta personalizada conforme o perfil da sua empresa.
            </p>
          </div>
        </Reveal>
      </div>
      <p className="mx-auto mt-8 max-w-2xl text-center text-sm text-muted-foreground md:text-base">
        Presença online é construção. Quanto mais constante for o trabalho, maior tende a ser a percepção de autoridade da empresa.
      </p>

      {/* Como funciona a contratação */}
      <div className="mx-auto mt-10 max-w-4xl">
        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="inline-flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/15 ring-1 ring-primary/40">
              <InfinityIcon className="size-5 text-primary" />
            </div>
            <div className="flex-1">
              <div className="text-xs font-semibold uppercase tracking-wider text-primary">Como funciona a contratação</div>
              <h3 className="mt-2 text-xl font-bold md:text-2xl">Fale no WhatsApp e sua primeira notícia sai em até 48h</h3>
              <ol className="mt-4 grid gap-3 text-sm md:grid-cols-3">
                <li className="rounded-xl border border-ink/10 shadow-[var(--shadow-soft)] bg-background/40 p-4">
                  <span className="mb-1 block text-xs font-bold text-primary">1. Fale conosco</span>
                  Clique em contratar pelo WhatsApp e converse direto com nossa equipe.
                </li>
                <li className="rounded-xl border border-ink/10 shadow-[var(--shadow-soft)] bg-background/40 p-4">
                  <span className="mb-1 block text-xs font-bold text-primary">2. Envie os dados</span>
                  Confirmamos a contratação e você envia as informações da sua empresa.
                </li>
                <li className="rounded-xl border border-ink/10 shadow-[var(--shadow-soft)] bg-background/40 p-4">
                  <span className="mb-1 block text-xs font-bold text-primary">3. Publicação em até 48h</span>
                  Sua primeira notícia entra no ar em até 48 horas úteis após o envio dos dados.
                </li>
              </ol>
              <a href={WA} target="_blank" rel="noopener" className="btn-primary mt-6 inline-flex">
                <MessageCircle className="size-4" /> Contratar pelo WhatsApp <ArrowRight className="size-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ───────────── For who ───────────── */
function ForWho() {
  const segments = [
    "Prestadores de serviço", "Clínicas", "Academias", "Óticas",
    "Cafeterias e restaurantes", "Empresas de guincho", "Dedetizadoras",
    "Assistências técnicas", "Construtoras", "Escritórios",
    "Profissionais liberais", "Negócios locais que atendem por cidade ou região",
  ];
  return (
    <Section>
      <SectionHeader eyebrow="Para quem é" title="Esse plano é ideal para empresas que querem ser mais encontradas" center />
      <ul className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {segments.map((s, i) => (
          <Reveal as="li" key={s} delay={(i % 3) as 0 | 1 | 2} className="flex items-center gap-3 rounded-xl border border-border shadow-[var(--shadow-soft)] bg-secondary/40 p-4 text-sm">
            <span className="inline-flex size-7 items-center justify-center rounded-full bg-primary/15 text-primary ring-1 ring-primary/30">
              <Building2 className="size-3.5" />
            </span>
            {s}
          </Reveal>
        ))}
      </ul>
      <p className="mx-auto mt-8 max-w-2xl text-center text-muted-foreground md:text-lg">
        Se seus clientes pesquisam no Google antes de chamar uma empresa, esse plano pode ajudar sua marca a parecer mais presente e confiável.
      </p>
    </Section>
  );
}

/* ───────────── Formulário de dados (pós-contratação) ───────────── */
function NeedToStart() {
  const [form, setForm] = useState({
    empresa: "",
    responsavel: "",
    whatsapp: "",
    email: "",
    cidade: "",
    regiao: "",
    servicos: "",
    descricao: "",
    diferenciais: "",
    site: "",
    instagram: "",
    ficha: "",
    observacoes: "",
  });

  const update = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildMessage = () => {
    const lines = [
      "*Novo cliente — Plano Autoridade Local (SVG)*",
      "",
      `*Plano contratado:* Mensal R$ 997`,
      `*Empresa:* ${form.empresa}`,
      `*Responsável:* ${form.responsavel}`,
      `*WhatsApp:* ${form.whatsapp}`,
      `*E-mail:* ${form.email}`,
      `*Cidade:* ${form.cidade}`,
      `*Região de atendimento:* ${form.regiao}`,
      `*Principais serviços:* ${form.servicos}`,
      `*Descrição da empresa:* ${form.descricao}`,
      `*Diferenciais:* ${form.diferenciais}`,
      `*Site:* ${form.site || "—"}`,
      `*Instagram:* ${form.instagram || "—"}`,
      `*Ficha do Google:* ${form.ficha || "—"}`,
      `*Observações:* ${form.observacoes || "—"}`,
      "",
      "Contratação já confirmada pelo WhatsApp. Aguardo a publicação da primeira notícia em até 48h.",
    ];
    return lines.join("\n");
  };

  const requiredOk =
    form.empresa && form.responsavel && form.whatsapp && form.cidade && form.servicos && form.descricao;

  const waHref = `https://wa.me/${SUPPORT_WA_NUMBER}?text=${encodeURIComponent(buildMessage())}`;

  const inputClass =
    "w-full rounded-lg border border-ink/10 bg-background/60 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/30";

  return (
    <Section id="formulario-dados" className="bg-[color:var(--color-sidebar)]">
      <SectionHeader
        eyebrow="Após a contratação"
        title="Preencha os dados da sua empresa"
        description="Depois de confirmar a contratação com nossa equipe no WhatsApp, envie este formulário. Com esses dados a SVG começa a produzir sua primeira notícia — no ar em até 48h."
        center
      />

      <div className="mx-auto mt-8 max-w-3xl">
        <div className="rounded-2xl border border-primary/30 bg-primary/[0.05] p-4 text-sm text-muted-foreground md:p-5">
          <p>
            <strong className="text-foreground">Importante:</strong> preencha e envie os dados <strong className="text-foreground">somente após confirmar a contratação</strong> com nossa equipe no WhatsApp. O envio abre uma conversa no WhatsApp com todos os campos já preenchidos.
          </p>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!requiredOk) return;
            window.open(waHref, "_blank", "noopener");
          }}
          className="mt-6 grid gap-4 rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-background/40 p-6 md:p-8"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Nome da empresa *</span>
              <input required value={form.empresa} onChange={update("empresa")} className={inputClass} placeholder="Ex.: Padaria Bom Pão" />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Responsável pelo contato *</span>
              <input required value={form.responsavel} onChange={update("responsavel")} className={inputClass} placeholder="Seu nome" />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">WhatsApp de atendimento *</span>
              <input required value={form.whatsapp} onChange={update("whatsapp")} className={inputClass} placeholder="(55) 99999-9999" />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">E-mail</span>
              <input type="email" value={form.email} onChange={update("email")} className={inputClass} placeholder="empresa@email.com" />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Cidade *</span>
              <input required value={form.cidade} onChange={update("cidade")} className={inputClass} placeholder="Cidade / UF" />
            </label>
            <label className="block text-sm md:col-span-2">
              <span className="mb-1 block font-medium">Região de atendimento</span>
              <input value={form.regiao} onChange={update("regiao")} className={inputClass} placeholder="Bairros, cidades vizinhas ou região metropolitana" />
            </label>
            <label className="block text-sm md:col-span-2">
              <span className="mb-1 block font-medium">Principais serviços *</span>
              <input required value={form.servicos} onChange={update("servicos")} className={inputClass} placeholder="Liste os serviços mais importantes" />
            </label>
            <label className="block text-sm md:col-span-2">
              <span className="mb-1 block font-medium">Descrição da empresa *</span>
              <textarea required rows={3} value={form.descricao} onChange={update("descricao")} className={inputClass} placeholder="Conte brevemente o que sua empresa faz, há quanto tempo atua, etc." />
            </label>
            <label className="block text-sm md:col-span-2">
              <span className="mb-1 block font-medium">Diferenciais</span>
              <textarea rows={2} value={form.diferenciais} onChange={update("diferenciais")} className={inputClass} placeholder="O que sua empresa faz melhor, prêmios, especializações, etc." />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Site</span>
              <input value={form.site} onChange={update("site")} className={inputClass} placeholder="https://" />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Instagram</span>
              <input value={form.instagram} onChange={update("instagram")} className={inputClass} placeholder="@perfil" />
            </label>
            <label className="block text-sm md:col-span-2">
              <span className="mb-1 block font-medium">Link da Ficha do Google</span>
              <input value={form.ficha} onChange={update("ficha")} className={inputClass} placeholder="Cole o link do Google Maps / Perfil da Empresa" />
            </label>
            <label className="block text-sm md:col-span-2">
              <span className="mb-1 block font-medium">Observações</span>
              <textarea rows={2} value={form.observacoes} onChange={update("observacoes")} className={inputClass} placeholder="Fotos, links, pautas sugeridas, tudo que quiser adicionar" />
            </label>
          </div>

          <p className="text-xs text-muted-foreground">
            Após enviar, o WhatsApp abrirá com todos os dados já preenchidos. Você só precisa confirmar o envio da mensagem. Se preferir, anexe fotos da empresa, equipe, fachada ou serviços na mesma conversa.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              disabled={!requiredOk}
              className="btn-primary disabled:cursor-not-allowed disabled:opacity-60"
            >
              <MessageCircle className="size-4" /> Enviar dados pelo WhatsApp
            </button>
            {!requiredOk && (
              <span className="text-xs text-muted-foreground">Preencha os campos com * para enviar.</span>
            )}
          </div>
        </form>

        {/* Suporte e atualizações */}
        <div className="mt-8 rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-background/40 p-6 md:p-7">
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Suporte e atualizações</div>
              <p className="mt-1 text-sm text-foreground md:text-base">
                Precisa de ajuda, quer acompanhar as publicações ou atualizar informações da empresa? Fale com o mesmo suporte oficial do CRM SVG.
              </p>
            </div>
            <a
              href={SUPPORT_WA}
              target="_blank"
              rel="noopener"
              className="btn-ghost whitespace-nowrap"
            >
              <MessageCircle className="size-4" /> Suporte no WhatsApp
            </a>
          </div>
        </div>
      </div>
    </Section>
  );
}

function ResultsCarousel() {
  const items = [
    { img: resultado1.url, period: "6 meses", impressoes: "120 → 680", cliques: "3 → 37", chamadas: "2 → 33", destaque: "+1.550% em chamadas" },
    { img: resultado2.url, period: "5 meses", impressoes: "110 → 510", cliques: "3 → 29", chamadas: "2 → 24", destaque: "+1.100% em chamadas" },
    { img: resultado3.url, period: "4 meses", impressoes: "150 → 420", cliques: "4 → 21", chamadas: "2 → 16", destaque: "+700% em chamadas" },
    { img: resultado4.url, period: "3 meses", impressoes: "90 → 260", cliques: "2 → 14", chamadas: "1 → 10", destaque: "+900% em chamadas" },
    { img: resultado5.url, period: "8 meses", impressoes: "220 → 1.120", cliques: "6 → 58", chamadas: "4 → 44", destaque: "+1.000% em chamadas" },
    { img: resultado6.url, period: "7 meses", impressoes: "180 → 860", cliques: "5 → 42", chamadas: "3 → 31", destaque: "+933% em chamadas" },
  ];
  return (
    <Section id="resultados-reais" className="bg-[color:var(--color-sidebar)]">
      <SectionHeader
        eyebrow="Prova social"
        title="Resultados reais de clientes SVG"
        description="Evolução de impressões, cliques e chamadas na Ficha do Google após meses de trabalho contínuo de presença online."
        center
      />
      <div className="mx-auto mt-10 max-w-6xl">
        <Carousel opts={{ align: "start", loop: true }} className="w-full">
          <CarouselContent className="-ml-4">
            {items.map((it, i) => (
              <CarouselItem key={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <div className="card-glass h-full !p-4">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary ring-1 ring-primary/30">
                      <TrendingUp className="size-3" /> {it.period}
                    </span>
                    <span className="text-[11px] font-semibold text-[oklch(0.52_0.13_72)]">
                      {it.destaque}
                    </span>
                  </div>
                  <div className="mt-3 overflow-hidden rounded-xl border border-ink/10 shadow-[var(--shadow-soft)] bg-white">
                    <img
                      src={it.img}
                      alt={`Resultado de cliente SVG em ${it.period}`}
                      loading="lazy"
                      className="h-auto w-full"
                    />
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg border border-ink/10 bg-background/40 p-2">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Impressões</div>
                      <div className="mt-0.5 text-xs font-semibold">{it.impressoes}</div>
                    </div>
                    <div className="rounded-lg border border-ink/10 bg-background/40 p-2">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Cliques</div>
                      <div className="mt-0.5 text-xs font-semibold">{it.cliques}</div>
                    </div>
                    <div className="rounded-lg border border-ink/10 bg-background/40 p-2">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Chamadas</div>
                      <div className="mt-0.5 text-xs font-semibold">{it.chamadas}</div>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex" />
          <CarouselNext className="hidden md:flex" />
        </Carousel>
        <p className="mt-6 text-center text-xs text-muted-foreground">
          Dados reais extraídos do Perfil da Empresa no Google de clientes atendidos pela SVG. Resultados variam conforme segmento, cidade e concorrência.
        </p>
      </div>
    </Section>
  );
}

/* ───────────── Testimonials ───────────── */
function Testimonials() {
  const items = [
    { q: "Depois que começamos a receber publicações mensais, sentimos que nossa empresa passou a ter uma presença muito mais profissional na internet.", n: "Cliente SVG", r: "Empresa de serviços locais" },
    { q: "O que mais gostei foi a praticidade. Enviei as informações da empresa e a SVG cuidou da criação e publicação dos conteúdos.", n: "Cliente SVG", r: "Negócio local" },
    { q: "Antes nossa empresa quase não tinha conteúdos publicados. Agora temos notícias todos os meses reforçando nossos serviços e região de atendimento.", n: "Cliente SVG", r: "Prestador de serviço" },
    { q: "Começaram a chegar mais clientes falando que acharam a gente no Google. A diferença na quantidade de contatos foi bem perceptível.", n: "Cliente SVG", r: "Assistência técnica" },
    { q: "Nossa ficha do Google subiu de posição nas buscas da cidade. Hoje aparecemos entre os primeiros quando alguém procura pelo nosso serviço.", n: "Cliente SVG", r: "Clínica local" },
    { q: "O site também começou a aparecer melhor nas buscas depois das publicações mensais. Sentimos que ganhamos mais autoridade online.", n: "Cliente SVG", r: "Prestador de serviço" },
    { q: "Pelo valor que a gente paga por mês, o retorno em visibilidade é muito grande. É um dos investimentos com melhor custo-benefício que já fizemos.", n: "Cliente SVG", r: "Empresa de serviços" },
    { q: "Achei o preço super acessível pra tudo que entrega. Notícias todo mês, mais presença no Google e clientes chegando — não tem como reclamar.", n: "Cliente SVG", r: "Negócio local" },
    { q: "Depois de uns meses percebi que muita gente já chegava dizendo 'vi vocês na internet'. A presença online mudou completamente.", n: "Cliente SVG", r: "Comércio local" },
    { q: "A ficha do Google e o site aparecem muito mais nas pesquisas da nossa região. E o custo mensal do plano é baixíssimo perto do resultado.", n: "Cliente SVG", r: "Prestador de serviço" },
    { q: "Passamos a ser encontrados com muito mais facilidade. O volume de mensagens no WhatsApp vindo do Google cresceu de forma clara.", n: "Cliente SVG", r: "Empresa local" },
  ];
  return (
    <Section>
      <SectionHeader eyebrow="Depoimentos" title="O que nossos clientes percebem" center />
      <div className="mt-12 grid gap-4 md:grid-cols-3">
        {items.map((t, i) => (
          <Reveal key={t.q} delay={(i % 3) as 0 | 1 | 2}>
            <Tilt3D>
              <div className="card-glass relative h-full">
                <div className="mb-3 flex gap-0.5 text-[oklch(0.52_0.13_72)]">
                  {Array.from({ length: 5 }).map((_, k) => <Star key={k} className="size-4 fill-current" />)}
                </div>
                <p className="text-sm leading-relaxed text-foreground">"{t.q}"</p>
                <div className="mt-6 border-t border-ink/10 pt-4">
                  <p className="text-sm font-semibold">{t.n}</p>
                  <p className="text-xs text-muted-foreground">{t.r}</p>
                </div>
              </div>
            </Tilt3D>
          </Reveal>
        ))}
      </div>
    </Section>
  );
}

/* ───────────── Final CTA ───────────── */
function FinalCTA() {
  return (
    <section className="py-16 md:py-24">
      <div className="container-svg">
        <div className="relative overflow-hidden rounded-3xl border border-primary/30 bg-[image:var(--gradient-hero)] p-10 md:p-16">
          <div aria-hidden className="absolute -right-24 -top-24 size-72 rounded-full bg-primary/20 blur-3xl" />
          <div aria-hidden className="absolute -bottom-24 -left-24 size-72 rounded-full bg-[oklch(0.82_0.16_85/0.15)] blur-3xl" />
          <div className="relative max-w-3xl">
            <h2 className="text-3xl font-bold tracking-tight md:text-5xl">
              <span className="text-gradient">
                Comece a fortalecer a presença online da sua empresa hoje
              </span>
            </h2>
            <p className="mt-5 text-base text-muted-foreground md:text-lg">
              Todos os meses, sua empresa pode ganhar novas notícias publicadas em portais nacionais, com conteúdo focado na sua cidade, região e serviços.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={WA} target="_blank" rel="noopener" className="btn-primary">
                <MessageCircle className="size-4" /> Contratar pelo WhatsApp <ArrowRight className="size-4" />
              </a>
            </div>
            <p className="mt-6 text-xs text-muted-foreground">
              Você envia os dados da empresa. A SVG cuida do resto.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
